// useMaintenanceVendorLinks.jsx
// Hook for generating and managing maintenance vendor links

import { getDatabase, ref, set, get, remove, update } from "firebase/database";
import { useAuth } from "./useAuth";
import { v4 as uuidv4 } from "uuid";

const useMaintenanceVendorLinks = () => {
  const { currentUser } = useAuth();
  const database = getDatabase();

  // Generate a vendor link with predefined slots
  const generateVendorLink = async (listingId, selectedSlots, linkName, extraDetails = null) => {
    if (!currentUser) {
      throw new Error("User is not authenticated.");
    }

    if (!listingId) {
      throw new Error("Listing ID is required.");
    }

    // Allow empty slots array - vendors will select their own time
    if (!selectedSlots || !Array.isArray(selectedSlots)) {
      throw new Error("Selected slots must be an array.");
    }

    try {
      const linkId = uuidv4();
      const linkUrl = `${window.location.origin}/maintenance-vendor/${listingId}/${linkId}`;

      // Build linkData object with proper structure
      const linkData = {
        linkId,
        listingId,
        linkName: linkName || null,
        status: "active",
        createdAt: Date.now(),
        createdBy: currentUser.uid,
        slots: selectedSlots.map(slot => {
          // Only include properties that are defined (Firebase doesn't allow undefined)
          const slotData = {
            id: slot.id || `slot-${Date.now()}`,
          };
          
          // Handle time properties - use startTime/endTime if available, otherwise start/end
          if (slot.startTime) slotData.startTime = slot.startTime;
          if (slot.endTime) slotData.endTime = slot.endTime;
          if (slot.start) slotData.start = slot.start;
          if (slot.end) slotData.end = slot.end;
          
          // Add optional properties only if they exist
          if (slot.day) slotData.day = slot.day;
          if (slot.date) slotData.date = slot.date;
          if (slot.displayText) slotData.displayText = slot.displayText;
          if (slot.type) slotData.type = slot.type;
          
          return slotData;
        }),
      };

      // Add extra details (linkRequirements and tenantDetails) to linkData if provided
      if (extraDetails) {
        if (extraDetails.linkRequirements) {
          linkData.linkRequirements = extraDetails.linkRequirements;
        }
        if (extraDetails.tenantDetails) {
          linkData.tenantDetails = extraDetails.tenantDetails;
        }
      }

      // Log the data structure being saved to Firebase
      console.log('[GENERATE VENDOR LINK] Saving to Firebase - Full linkData structure:', {
        path: `users/${currentUser.uid}/vendorLinks/${linkId}`,
        linkData: {
          linkId: linkData.linkId,
          listingId: linkData.listingId,
          linkName: linkData.linkName,
          status: linkData.status,
          createdAt: linkData.createdAt,
          createdBy: linkData.createdBy,
          linkRequirements: linkData.linkRequirements || null,
          tenantDetails: linkData.tenantDetails || null,
          slots: linkData.slots,
        },
        timestamp: new Date().toISOString(),
      });

      // Save to: users/{userId}/vendorLinks/{linkId}
      const linkRef = ref(database, `users/${currentUser.uid}/vendorLinks/${linkId}`);
      await set(linkRef, linkData);

      const listingLinkData = {
        linkId,
        listingId,
        createdBy: currentUser.uid,
        status: "active",
        createdAt: Date.now(),
      };

      console.log('[GENERATE VENDOR LINK] Saving to Firebase - Listing reference:', {
        path: `listings/${listingId}/vendorLinks/${linkId}`,
        listingLinkData,
        timestamp: new Date().toISOString(),
      });

      // Also save a reference at listing level for public access
      // This allows the link to be found without knowing the user who created it
      const listingLinkRef = ref(database, `listings/${listingId}/vendorLinks/${linkId}`);
      await set(listingLinkRef, listingLinkData);

      console.log('[GENERATE VENDOR LINK] Successfully saved to Firebase:', {
        linkId,
        linkUrl,
        timestamp: new Date().toISOString(),
      });

      return { linkId, linkUrl };
    } catch (error) {
      console.error("Error generating vendor link:", error);
      throw error;
    }
  };

  // Get all vendor links for the current user
  const getAllVendorLinks = async () => {
    if (!currentUser) {
      throw new Error("User is not authenticated.");
    }

    try {
      const linksRef = ref(database, `users/${currentUser.uid}/vendorLinks`);
      const snapshot = await get(linksRef);

      if (!snapshot.exists()) {
        return [];
      }

      const links = Object.entries(snapshot.val()).map(([id, link]) => ({
        id,
        ...link,
      }));

      return links;
    } catch (error) {
      console.error("Error fetching vendor links:", error);
      throw error;
    }
  };

  // Get a specific vendor link (public access - no auth required)
  const getVendorLink = async (listingId, linkId) => {
    if (!listingId || !linkId) {
      throw new Error("Listing ID and Link ID are required.");
    }

    try {
      // First, check the listing-level reference to get the creator's UID
      const listingLinkRef = ref(database, `listings/${listingId}/vendorLinks/${linkId}`);
      const listingLinkSnapshot = await get(listingLinkRef);

      if (!listingLinkSnapshot.exists()) {
        throw new Error("Vendor link not found.");
      }

      const listingLinkData = listingLinkSnapshot.val();
      
      // Verify the link belongs to this listing
      if (listingLinkData.listingId !== listingId) {
        throw new Error("Vendor link does not belong to this listing.");
      }

      // Check if link is active
      if (listingLinkData.status !== "active") {
        throw new Error("Vendor link is no longer active.");
      }

      // Get the full link data from the creator's vendorLinks
      if (listingLinkData.createdBy) {
        const userLinkRef = ref(database, `users/${listingLinkData.createdBy}/vendorLinks/${linkId}`);
        const userLinkSnapshot = await get(userLinkRef);
        
        if (userLinkSnapshot.exists()) {
          const fullLinkData = userLinkSnapshot.val();
          // Verify it matches the listing
          if (fullLinkData.listingId === listingId && fullLinkData.status === "active") {
            return { id: linkId, ...fullLinkData };
          }
        }
      }

      // If we can't get the full data, return what we have from the listing reference
      // This is a fallback - ideally we should always have the full data
      throw new Error("Vendor link data incomplete.");
    } catch (error) {
      console.error("Error fetching vendor link:", error);
      throw error;
    }
  };

  // Delete a vendor link
  const deleteVendorLink = async (listingId, linkId) => {
    if (!currentUser) {
      throw new Error("User is not authenticated.");
    }

    try {
      const linkRef = ref(database, `users/${currentUser.uid}/vendorLinks/${linkId}`);
      const snapshot = await get(linkRef);

      if (!snapshot.exists()) {
        throw new Error("Vendor link does not exist.");
      }

      const linkData = snapshot.val();
      if (linkData.listingId !== listingId) {
        throw new Error("Link does not belong to the specified listing.");
      }

      if (linkData.createdBy !== currentUser.uid) {
        throw new Error("You do not have permission to delete this link.");
      }

      await remove(linkRef);

      // Also remove from listing's vendorLinks
      const listingLinkRef = ref(database, `listings/${listingId}/vendorLinks/${linkId}`);
      await remove(listingLinkRef);
    } catch (error) {
      console.error("Error deleting vendor link:", error);
      throw error;
    }
  };

  // Update link status (active/inactive)
  const updateLinkStatus = async (listingId, linkId, status) => {
    if (!currentUser) {
      throw new Error("User is not authenticated.");
    }

    if (!["active", "inactive"].includes(status)) {
      throw new Error("Status must be 'active' or 'inactive'.");
    }

    try {
      const linkRef = ref(database, `users/${currentUser.uid}/vendorLinks/${linkId}`);
      const snapshot = await get(linkRef);

      if (!snapshot.exists()) {
        throw new Error("Vendor link does not exist.");
      }

      const linkData = snapshot.val();
      if (linkData.listingId !== listingId) {
        throw new Error("Link does not belong to the specified listing.");
      }

      if (linkData.createdBy !== currentUser.uid) {
        throw new Error("You do not have permission to update this link.");
      }

      await update(linkRef, { status });

      // Also update the listing-level reference
      const listingLinkRef = ref(database, `listings/${listingId}/vendorLinks/${linkId}`);
      await update(listingLinkRef, { status });
    } catch (error) {
      console.error("Error updating vendor link status:", error);
      throw error;
    }
  };

  return {
    generateVendorLink,
    getAllVendorLinks,
    getVendorLink,
    deleteVendorLink,
    updateLinkStatus,
  };
};

export default useMaintenanceVendorLinks;

